import { useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { useWhoAmI } from "../lib/api";
import { ROLE_LABEL_KEYS } from "../lib/auth";
import { useOnlineStatus } from "../lib/online";
import { AssistantButton, AssistantPanel } from "./AssistantPanel";
import { BrandFooterLine, BrandLogo } from "./Brand";
import { Breadcrumbs } from "./Breadcrumbs";
import { NotificationsBell } from "./fleet/NotificationsBell";
import { HelpGuideButton } from "./HelpGuide";

const LANGS = ["fr", "en", "ln"] as const;

/** The language the person chose (not the one i18next resolved to — Lingala falls back to French for missing terms). */
export function currentLanguage(lng: string | undefined): (typeof LANGS)[number] {
  const code = (lng || "fr").slice(0, 2);
  return (LANGS as readonly string[]).includes(code) ? (code as (typeof LANGS)[number]) : "fr";
}

export function LanguageSwitch() {
  const { t, i18n } = useTranslation();
  const current = currentLanguage(i18n.language);
  useEffect(() => { document.documentElement.lang = current; }, [current]);
  return (
    <nav aria-label="language" className="flex items-center gap-1 text-xs">
      {LANGS.map((lng) => (
        <button key={lng} type="button" onClick={() => void i18n.changeLanguage(lng)} title={t(`lang.${lng}`)} aria-pressed={current === lng} data-lang={lng}
          className={`rounded px-2 py-1 font-semibold ${current === lng ? "bg-brand-primary text-white" : "text-brand-grey hover:bg-brand-grey-light"}`}>
          {lng.toUpperCase()}
        </button>
      ))}
    </nav>
  );
}

/** One-line notice while the Lingala glossary is under review with the customer (WP-16.7). */
function LanguageNotice() {
  const { t, i18n } = useTranslation();
  if (currentLanguage(i18n.language) !== "ln") return null;
  return <div className="border-b border-amber-200 bg-amber-50 px-6 py-1 text-center text-[11px] text-amber-900" data-lang-notice>{t("lang.draft")}</div>;
}

/** Connectivity banner (WP-03.1): the screens are read-mostly, so losing the network usually means the boards
 * quietly stop refreshing rather than failing loudly — this says so. Screens that can still take work while
 * offline (the POS till) say so themselves; this is the group-wide "what you see may be stale" notice. */
function OfflineNotice() {
  const { t } = useTranslation();
  if (useOnlineStatus()) return null;
  return <div className="border-b border-status-red/30 bg-status-red/10 px-6 py-1 text-center text-[11px] font-semibold text-status-red" data-offline-notice>{t("common.offlineNotice")}</div>;
}

export function Brand({ subtitle }: { subtitle?: string }) {
  return (
    <Link to="/" className="flex items-center gap-3">
      <BrandLogo variant="light" className="h-9" />
      <span className="flex flex-col leading-tight">
        <span className="font-heading text-lg font-semibold text-brand-primary">OneSphere</span>
        {subtitle && <span className="text-[11px] uppercase tracking-wide text-brand-grey">{subtitle}</span>}
      </span>
    </Link>
  );
}

export function UserMenu() {
  const { t } = useTranslation();
  const { identity, signOut } = useAuth();
  const whoami = useWhoAmI();
  if (!identity) return null;
  const roles = identity.roles.map((r) => t(ROLE_LABEL_KEYS[r] ?? r)).join(" · ");
  // scope = User Permissions on Company / Plant synced from the token's /Scope/... groups (WP-02.3)
  const scopeValues = Object.values(whoami.data?.scope ?? {}).flat();
  const scope = scopeValues.length ? scopeValues.join(" · ") : t("common.group");
  return (
    <div className="flex items-center gap-3">
      <div className="hidden text-right sm:block">
        <div className="text-sm font-semibold text-brand-charcoal">{identity.name}</div>
        <div className="max-w-[28rem] truncate text-[11px] text-brand-grey" title={roles}>{roles || identity.email}</div>
        <div className="max-w-[28rem] truncate text-[11px] text-brand-primary" title={scope}>{t("common.scope")}: {scope}</div>
      </div>
      <button onClick={() => void signOut()}
        className="rounded-md border border-brand-grey-light px-3 py-1.5 text-xs font-semibold text-brand-charcoal hover:border-brand-primary hover:text-brand-primary">
        {t("auth.signOut")}
      </button>
    </div>
  );
}

/** Page frame: header, breadcrumb bar (every page but the landing hub), the page, assistant drawer, footer. `record` names the
 * current document in the trail when the page knows a better label than the route segment. */
export function AppShell({ children, subtitle, actions, record }: { children: ReactNode; subtitle?: string; actions?: ReactNode; record?: string }) {
  const { pathname } = useLocation();
  return (
    <div className="flex min-h-screen flex-col bg-[#F6F8FA] text-brand-charcoal">
      <header className="sticky top-0 z-10 border-b border-brand-grey-light bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3">
          <div className="shrink-0"><Brand subtitle={subtitle} /></div>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-4 gap-y-1">
            {actions}
            <HelpGuideButton />
            <AssistantButton />
            <NotificationsBell />
            <LanguageSwitch />
            <UserMenu />
          </div>
        </div>
      </header>
      {pathname !== "/" && <Breadcrumbs record={record} />}
      <OfflineNotice />
      <LanguageNotice />
      <main className="mx-auto w-full max-w-7xl flex-1 px-6 py-6">{children}</main>
      <AssistantPanel />
      <footer className="border-t border-brand-grey-light bg-white px-6 py-3">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 text-[11px] tracking-wide text-brand-grey">
          <BrandFooterLine />
          <span className="ml-auto flex items-center gap-2">
            <span>Powered by</span>
            <img src="/brand/ethx-logo.png" alt="ETHX Softcon" className="h-5" />
          </span>
        </div>
      </footer>
    </div>
  );
}
